import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SeatMapView, useSeatSelection } from '@bre-ticketing/seatmap';
import type { ManifestGeometry, SeatGeometry } from '@bre-ticketing/seatmap';
import type { AdminExchangeQuote, ApiError, InventoryUnitRow, ManifestLayout, OrderDetail } from '../api';
import { adminExchangeEndpoints, endpoints, inventoryEndpoints } from '../api';
import { loadStripe } from '../stripe';
import { Button, Panel } from '../ui';
import { INPUT, dollars, inZone } from './event/shared';
import { dateWords } from '../terms';

/**
 * Exchanging from the order page (D-66): pick the tickets in the table,
 * choose the performance and either best-available seats (with a section
 * preference) or exact seats on the map, quote, then settle the
 * difference: a keyed card, a check, a house account, a manager's waiver,
 * a refund to the original tender, or a forfeit. The policy's fee applies
 * unless a manager waives it.
 */

type Difference = 'PAY' | 'WAIVE' | 'REFUND' | 'FORFEIT';
type Tender = { tender_type: 'CHECK'; amount_cents: number; check_number: string } | { tender_type: 'HOUSE_ACCOUNT'; amount_cents: number; organization_id: string } | { tender_type: 'CARD_KEYED'; amount_cents: number; payment_intent_id: string };

export function OrderExchange({ order, selectedTicketIds, canWaive, canHouseAccount, onClose, onDone, onError }: { order: OrderDetail; selectedTicketIds: string[]; canWaive: boolean; canHouseAccount: boolean; onClose: () => void; onDone: (message: string) => void; onError: (message: string) => void }) {
  const ticketIds = selectedTicketIds;
  const options = useQuery({ queryKey: ['exchange-options', order.id, ticketIds.join(',')], queryFn: () => adminExchangeEndpoints.options(order.id, ticketIds), enabled: ticketIds.length > 0, retry: false });
  const [performanceId, setPerformanceId] = useState('');
  const [ticketTypeId, setTicketTypeId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [mode, setMode] = useState<'best' | 'seats'>('best');
  const [held, setHeld] = useState(0);
  const [waiveFee, setWaiveFee] = useState(false);
  const [difference, setDifference] = useState<Difference | null>(null);
  const [tenderKind, setTenderKind] = useState<'CARD_KEYED' | 'CHECK' | 'HOUSE_ACCOUNT'>('CARD_KEYED');
  const [checkNumber, setCheckNumber] = useState('');
  const [orgId, setOrgId] = useState('');
  const [card, setCard] = useState<{ intent_id: string; ready: boolean } | null>(null);
  const [reason, setReason] = useState('');
  const [email, setEmail] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const elementsRef = useRef<{ stripe: Awaited<ReturnType<typeof loadStripe>>; elements: ReturnType<Awaited<ReturnType<typeof loadStripe>>['elements']> } | null>(null);
  const selection = useSeatSelection();

  useEffect(() => {
    if (!options.data) return;
    if (!performanceId && options.data.performances[0]) setPerformanceId(options.data.performances[0].id);
    if (!ticketTypeId && options.data.ticket_types[0]) setTicketTypeId(options.data.ticket_types[0].id);
    setHeld(options.data.held.length);
  }, [options.data, performanceId, ticketTypeId]);

  const performance = options.data?.performances.find((p) => p.id === performanceId) ?? null;
  const layout = useQuery<ManifestLayout>({ queryKey: ['manifest-layout', performance?.manifest_id], queryFn: () => endpoints.manifestLayout(performance!.manifest_id), enabled: Boolean(performance) && mode === 'seats' });
  const units = useQuery({ queryKey: ['inventory-units', options.data?.event_id, performanceId], queryFn: () => inventoryEndpoints.units(options.data!.event_id, performanceId), enabled: Boolean(options.data) && Boolean(performanceId) && mode === 'seats', refetchInterval: 5000 });
  const unitBySeat = useMemo(() => new Map((units.data?.units ?? []).filter((u: InventoryUnitRow) => u.seat_id).map((u: InventoryUnitRow) => [u.seat_id!, u])), [units.data]);
  const seatFills = useMemo(() => {
    const fills: Record<string, string> = {};
    for (const [seatId, unit] of unitBySeat) fills[seatId] = unit.state === 'AVAILABLE' && !unit.cart_held ? '#7ac47f' : '#d9d9d9';
    return fills;
  }, [unitBySeat]);
  const selectedUnitIds = useMemo(() => Array.from(selection.selected, (seatId) => unitBySeat.get(seatId)?.unit_id).filter((id): id is string => Boolean(id)), [selection.selected, unitBySeat]);
  const geometry = layout.data as unknown as ManifestGeometry | undefined;

  const quote = useQuery<{ quote: AdminExchangeQuote }, ApiError>({ queryKey: ['exchange-quote', order.id, ticketIds.join(','), held, waiveFee], queryFn: () => adminExchangeEndpoints.quote(order.id, ticketIds, waiveFee), enabled: held === ticketIds.length && ticketIds.length > 0, retry: false });
  const q = quote.data?.quote ?? null;
  const diff = q?.difference_cents ?? 0;
  const policy: Difference = difference ?? (diff > 0 ? 'PAY' : diff < 0 ? (q?.downgrade ?? 'REFUND') : 'PAY');

  const hold = useMutation({
    mutationFn: () =>
      adminExchangeEndpoints.hold(order.id, { ticket_ids: ticketIds, performance_id: performanceId, ticket_type_id: ticketTypeId, mode, ...(mode === 'seats' ? { unit_ids: selectedUnitIds } : { quantity: ticketIds.length - held, ...(sectionId ? { section_ids: [sectionId] } : {}) }) }),
    onSuccess: (r) => {
      setError(null);
      setHeld(r.held_total);
      selection.clear();
      setCard(null);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const release = useMutation({
    mutationFn: () => adminExchangeEndpoints.release(order.id),
    onSuccess: () => {
      setHeld(0);
      setCard(null);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const startCard = async () => {
    if (!q) return;
    setError(null);
    try {
      const started = await adminExchangeEndpoints.card(order.id, diff);
      if (!started.client_secret || !started.publishable_key) throw new Error('Stripe did not return a card form');
      const stripe = await loadStripe(started.publishable_key);
      const elements = stripe.elements({ clientSecret: started.client_secret });
      elementsRef.current = { stripe, elements };
      setCard({ intent_id: started.payment_intent_id, ready: false });
      window.setTimeout(() => elements.create('payment', { layout: 'tabs' }).mount('#admin-keyed-card'), 0);
    } catch (cause) {
      setError((cause as Error).message);
    }
  };
  const authorizeCard = async () => {
    if (!elementsRef.current || !card) return;
    const { stripe, elements } = elementsRef.current;
    const result = await stripe.confirmPayment({ elements, confirmParams: { return_url: window.location.href }, redirect: 'if_required' });
    if (result.error) return setError(result.error.message ?? 'The card was declined');
    setCard({ ...card, ready: true });
  };
  const complete = useMutation({
    mutationFn: () => {
      if (!q) throw new Error('No quote yet');
      const tenders: Tender[] = [];
      if (policy === 'PAY' && diff > 0) {
        if (tenderKind === 'CARD_KEYED') {
          if (!card?.ready) throw new Error('Authorize the card first');
          tenders.push({ tender_type: 'CARD_KEYED', amount_cents: diff, payment_intent_id: card.intent_id });
        } else if (tenderKind === 'CHECK') {
          if (!checkNumber.trim()) throw new Error('Enter the check number');
          tenders.push({ tender_type: 'CHECK', amount_cents: diff, check_number: checkNumber.trim() });
        } else {
          if (!orgId.trim()) throw new Error('Enter the organization id for the house account');
          tenders.push({ tender_type: 'HOUSE_ACCOUNT', amount_cents: diff, organization_id: orgId.trim() });
        }
      }
      return adminExchangeEndpoints.complete(order.id, { ticket_ids: ticketIds, difference: policy, tenders, waive_fee: waiveFee, reason: reason.trim() || null, email });
    },
    onSuccess: (r) => {
      const x = r.exchange;
      onDone(`Exchanged ${x.new_ticket_ids.length} ticket${x.new_ticket_ids.length === 1 ? '' : 's'}${x.amount_due_cents ? `; collected ${dollars(x.amount_due_cents)}` : ''}${x.amount_refunded_cents ? `; refunded ${dollars(x.amount_refunded_cents)}${x.refund && !x.refund.stripe.ok ? ` (STRIPE REFUND FAILED: ${x.refund.stripe.message ?? ''})` : ''}` : ''}${x.adjustment_cents < 0 ? `; waived ${dollars(-x.adjustment_cents)}` : x.adjustment_cents > 0 ? `; forfeited ${dollars(x.adjustment_cents)}` : ''}${x.fee_cents ? `; fee ${dollars(x.fee_cents)}` : ''}.`);
    },
    onError: (cause: Error) => onError(cause.message),
  });

  if (ticketIds.length === 0) {
    return (
      <Panel title="Exchange" description="Tick the tickets to exchange in the items table below, then choose their replacements here.">
        <div className="px-5 py-4">
          <Button onClick={onClose}>Cancel</Button>
        </div>
      </Panel>
    );
  }
  return (
    <Panel title="Exchange" description={`Swapping ${ticketIds.length} ticket${ticketIds.length === 1 ? '' : 's'} for other seats of the same event.${options.data?.policy.name ? ` Policy ${options.data.policy.name}: fee ${dollars(options.data.policy.fee_cents)} per ticket, downgrades ${options.data.policy.downgrade.toLowerCase()}.` : ' No policy: no fee, downgrades refunded.'}`}>
      <div className="space-y-4 px-5 py-4 text-sm">
        {error ? <p className="rounded border border-bad/30 bg-bad/10 px-3 py-2 text-bad">{error}</p> : null}
        {options.error ? <p className="text-bad">{options.error.message}</p> : null}
        {options.data && held < ticketIds.length ? (
          <div className="space-y-3 rounded border border-line bg-page/60 p-3">
            <p className="font-medium text-ink-900">
              Replacement seats: {held} of {ticketIds.length} held
            </p>
            <div className="flex flex-wrap gap-3">
              <label>
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">{dateWords().One}</span>
                <select value={performanceId} onChange={(e) => setPerformanceId(e.target.value)} className={INPUT}>
                  {options.data.performances.map((p) => (
                    <option key={p.id} value={p.id}>
                      {inZone(p.starts_at, 'America/Chicago')} · {p.status.toLowerCase().replaceAll('_', ' ')}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Ticket type</span>
                <select value={ticketTypeId} onChange={(e) => setTicketTypeId(e.target.value)} className={INPUT}>
                  {options.data.ticket_types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">How</span>
                <select value={mode} onChange={(e) => setMode(e.target.value as 'best' | 'seats')} className={INPUT}>
                  <option value="best">Best available</option>
                  <option value="seats">Pick on the map</option>
                </select>
              </label>
              {mode === 'best' ? (
                <label>
                  <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Section</span>
                  <select value={sectionId} onChange={(e) => setSectionId(e.target.value)} className={INPUT}>
                    <option value="">Anywhere</option>
                    {options.data.sections.filter((s) => s.performance_id === performanceId).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
            {mode === 'seats' ? (
              geometry ? (
                <SeatMapView layout={geometry} mode="edit" backgroundUrl={layout.data?.manifest.background_url ?? null} seatFills={seatFills} selectedSeatIds={selection.selected} onSeatClick={(seat: SeatGeometry, modifiers: { shiftKey: boolean; metaKey: boolean }) => selection.clickSeat(seat, modifiers)} onMarqueeSelect={selection.marquee} className="overflow-hidden rounded border border-line" />
              ) : (
                <p className="text-ink-400">Loading map…</p>
              )
            ) : null}
            <Button variant="primary" disabled={hold.isPending || (mode === 'seats' && selectedUnitIds.length !== ticketIds.length - held)} onClick={() => hold.mutate()}>
              {mode === 'seats' ? `Hold ${selectedUnitIds.length} selected seat${selectedUnitIds.length === 1 ? '' : 's'}` : `Hold ${ticketIds.length - held} best available`}
            </Button>
          </div>
        ) : null}
        {held > 0 ? (
          <p className="text-ink-600">
            {held} replacement seat{held === 1 ? '' : 's'} held for this exchange.{' '}
            <button type="button" className="underline" onClick={() => release.mutate()}>
              Release
            </button>
          </p>
        ) : null}
        {quote.error ? <p className="text-bad">{quote.error.message}</p> : null}
        {q ? (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded border border-line p-3">
                <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Out</p>
                {q.out.map((t) => (
                  <p key={t.ticket_id} className="flex justify-between">
                    <span>
                      {t.seat_label} <span className="text-ink-400">{t.ticket_type ?? ''}</span>
                    </span>
                    <span className="tabular">{dollars(t.value_cents)}</span>
                  </p>
                ))}
              </div>
              <div className="rounded border border-line p-3">
                <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">In</p>
                {q.in.map((t) => (
                  <p key={t.unit_id} className="flex justify-between">
                    <span>
                      {t.seat_label} <span className="text-ink-400">{t.ticket_type ?? ''} · {inZone(t.performance_starts_at, 'America/Chicago')}</span>
                    </span>
                    <span className="tabular">{dollars(t.value_cents)}</span>
                  </p>
                ))}
                {q.fee_cents > 0 ? (
                  <p className="flex justify-between text-ink-600">
                    <span>Exchange fee</span>
                    <span className="tabular">{dollars(q.fee_cents)}</span>
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <p className="tabular text-xl font-bold text-ink-900">{diff === 0 ? 'Even' : diff > 0 ? `${dollars(diff)} due` : `${dollars(-diff)} owed to the customer`}</p>
              {q.policy_fee_cents > 0 && canWaive ? (
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={waiveFee} onChange={(e) => setWaiveFee(e.target.checked)} /> Waive the {dollars(q.policy_fee_cents)} fee
                </label>
              ) : null}
            </div>
            {diff !== 0 ? (
              <fieldset className="space-y-1 rounded border border-line p-3">
                <legend className="px-1 text-xs font-semibold tracking-wide text-ink-400 uppercase">The difference</legend>
                {diff > 0 ? (
                  <>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="adm-diff" checked={policy === 'PAY'} onChange={() => setDifference('PAY')} /> Collect {dollars(diff)}
                    </label>
                    {canWaive ? (
                      <label className="flex items-center gap-2">
                        <input type="radio" name="adm-diff" checked={policy === 'WAIVE'} onChange={() => setDifference('WAIVE')} /> Waive it
                      </label>
                    ) : null}
                  </>
                ) : (
                  <>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="adm-diff" checked={policy === 'REFUND'} onChange={() => setDifference('REFUND')} /> Refund {dollars(-diff)} to the original tender
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="adm-diff" checked={policy === 'FORFEIT'} onChange={() => setDifference('FORFEIT')} /> Forfeited
                    </label>
                  </>
                )}
              </fieldset>
            ) : null}
            {policy === 'PAY' && diff > 0 ? (
              <div className="space-y-2 rounded border border-line p-3">
                <div className="flex flex-wrap gap-1">
                  {(['CARD_KEYED', 'CHECK', 'HOUSE_ACCOUNT'] as const).filter((k) => k !== 'HOUSE_ACCOUNT' || canHouseAccount).map((k) => (
                    <button key={k} type="button" onClick={() => setTenderKind(k)} className={`rounded px-3 py-1 text-sm ${tenderKind === k ? 'bg-accent text-white' : 'border border-line'}`}>
                      {{ CARD_KEYED: 'Card (keyed)', CHECK: 'Check', HOUSE_ACCOUNT: 'House account' }[k]}
                    </button>
                  ))}
                </div>
                {tenderKind === 'CARD_KEYED' ? (
                  card ? (
                    <div>
                      <div id="admin-keyed-card" className="rounded border border-line p-3" />
                      <Button variant="primary" className="mt-2" disabled={card.ready} onClick={() => void authorizeCard()}>
                        {card.ready ? `Authorized ${dollars(diff)}` : `Authorize ${dollars(diff)}`}
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={() => void startCard()}>Key a card for {dollars(diff)}</Button>
                  )
                ) : null}
                {tenderKind === 'CHECK' ? <input value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} placeholder="Check number" className={INPUT} aria-label="Check number" /> : null}
                {tenderKind === 'HOUSE_ACCOUNT' ? <input value={orgId} onChange={(e) => setOrgId(e.target.value)} placeholder="Organization id" className={INPUT} aria-label="Organization" /> : null}
              </div>
            ) : null}
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex-1">
                <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Reason (recorded)</span>
                <input value={reason} onChange={(e) => setReason(e.target.value)} className={`${INPUT} w-full`} />
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> Email the new tickets
              </label>
            </div>
          </>
        ) : null}
        <div className="flex gap-2">
          <Button variant="primary" disabled={!q || complete.isPending} onClick={() => complete.mutate()}>
            {complete.isPending ? 'Exchanging…' : 'Complete exchange'}
          </Button>
          <Button
            onClick={() => {
              if (held > 0) release.mutate();
              onClose();
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Panel>
  );
}
